import { useMemo, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import {
  BRAND_SOCIAL_CONTENT_STYLES,
  BRAND_SOCIAL_MECHANICS,
  SOCIAL_POST_CAROUSEL_MAX_SLIDES,
  SOCIAL_POST_CAROUSEL_MIN_SLIDES,
  SOCIAL_POST_CAROUSEL_SLIDE_ROLES,
  type BrandSocialContentStyle,
  type BrandSocialMechanic,
} from "@runcheetah/shared";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
} from "@runcheetah/ui";

import { EmptyState, InfoTooltip, QueryErrorState, Select, Textarea } from "@/components/common";
import { LayoutTemplateCanvasPreview } from "@/components/social-post-layout-template-canvas-preview";
import { formatSnakeLabel } from "@/lib/labels";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  buildCarouselFormatWriteInput,
  createCarouselFormatDraftFromOutput,
  createCarouselPatternRowDraft,
  createEmptyCarouselFormatDraft,
  moveArrayItem,
  moveCarouselFormatId,
  validateCarouselFormatDraft,
  type CarouselFormatDraft,
  type CarouselFormatPatternRowDraft,
} from "./carousel-formats-state";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type CarouselFormat = RouterOutputs["admin"]["carouselFormats"]["list"][number];
type LayoutTemplate = RouterOutputs["admin"]["socialPostLayoutTemplates"]["list"][number];
type LayoutTemplateById = Map<string, LayoutTemplate>;
type TemplateOptions = CarouselFormatDraft["templateOptions"];

const getNextSortOrder = (formats: CarouselFormat[]): number =>
  formats.reduce((max, format) => Math.max(max, format.sortOrder), 0) + 10;

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

const FieldLabelWithHelp = ({
  children,
  tooltipLabel,
  tooltipContent,
}: {
  children: React.ReactNode;
  tooltipLabel: string;
  tooltipContent: React.ReactNode;
}) => (
  <span className="inline-flex items-center gap-1.5">
    <FieldLabel>{children}</FieldLabel>
    <InfoTooltip label={tooltipLabel} content={tooltipContent} className="size-4" />
  </span>
);

const getLayoutTemplatePreviewDefinition = (template: LayoutTemplate | undefined) =>
  template?.publishedTemplate ?? template?.draftTemplate ?? null;

const LayoutTemplateThumbnail = ({
  template,
  label,
  fallbackTitle,
  className,
  maxWidth = 80,
}: {
  template?: LayoutTemplate;
  label: string;
  fallbackTitle: string;
  className?: string;
  maxWidth?: number;
}) => {
  const definition = getLayoutTemplatePreviewDefinition(template);

  if (definition) {
    return (
      <LayoutTemplateCanvasPreview
        template={definition}
        label={label}
        className={className}
        maxWidth={maxWidth}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={label}
      className={cn(
        "flex items-center justify-center overflow-hidden rounded-lg border border-dashed bg-muted/30 p-2 text-center text-[0.65rem] leading-tight text-muted-foreground",
        className,
      )}
    >
      <span className="line-clamp-3">{fallbackTitle}</span>
    </div>
  );
};

const AssignedTemplatePreviewPager = ({
  templateOptions,
  templatesById,
  slideLabel,
  className,
}: {
  templateOptions: TemplateOptions;
  templatesById: LayoutTemplateById;
  slideLabel: string;
  className?: string;
}) => {
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const templateCount = templateOptions.length;
  const selectedTemplateIndex =
    templateCount > 0 ? Math.min(activeTemplateIndex, templateCount - 1) : 0;
  const selectedOption = templateOptions[selectedTemplateIndex] ?? null;
  const selectedTemplate = selectedOption
    ? templatesById.get(selectedOption.templateId)
    : undefined;

  const moveTemplatePreview = (direction: -1 | 1) => {
    if (templateCount <= 1) {
      return;
    }

    setActiveTemplateIndex(
      (current) =>
        (Math.min(current, templateCount - 1) + direction + templateCount) % templateCount,
    );
  };

  return (
    <div className={cn("rounded-md bg-muted/30 p-2", className)}>
      <LayoutTemplateThumbnail
        template={selectedTemplate}
        label={
          selectedOption
            ? `${slideLabel} template preview: ${selectedOption.templateName}`
            : `${slideLabel} layout template preview`
        }
        fallbackTitle={selectedOption?.templateName ?? "Any template"}
        className="h-[5.5rem] w-full"
        maxWidth={96}
      />
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[0.68rem] leading-tight text-muted-foreground">
        <span className="min-w-0 truncate font-medium text-foreground">
          {selectedOption?.templateName ?? "Any template"}
        </span>
        {templateCount > 1 ? (
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={`Show previous template for ${slideLabel}`}
              className="rounded border bg-background p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => moveTemplatePreview(-1)}
            >
              <ChevronLeft className="size-3" />
            </button>
            <span aria-label={`${selectedTemplateIndex + 1} of ${templateCount} templates`}>
              {selectedTemplateIndex + 1}/{templateCount}
            </span>
            <button
              type="button"
              aria-label={`Show next template for ${slideLabel}`}
              className="rounded border bg-background p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => moveTemplatePreview(1)}
            >
              <ChevronRight className="size-3" />
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
};

export function CarouselFormatsPage() {
  const utils = trpc.useUtils();
  const formatsQuery = trpc.admin.carouselFormats.list.useQuery({ activeOnly: false });
  const layoutTemplatesQuery = trpc.admin.socialPostLayoutTemplates.list.useQuery({
    publishedOnly: true,
  });
  const formats = formatsQuery.data ?? [];
  const publishedLayoutTemplates = layoutTemplatesQuery.data ?? [];
  const [draft, setDraft] = useState<CarouselFormatDraft>(() => createEmptyCarouselFormatDraft(10));
  const [activePatternIndex, setActivePatternIndex] = useState(0);

  const validationErrors = useMemo(() => validateCarouselFormatDraft(draft), [draft]);
  const selectedFormat = draft.id ? formats.find((format) => format.id === draft.id) : null;
  const isEditing = Boolean(selectedFormat);
  const selectedPatternIndex = Math.min(activePatternIndex, Math.max(0, draft.pattern.length - 1));
  const selectedPatternRow = draft.pattern[selectedPatternIndex] ?? null;
  const publishedLayoutTemplatesById = useMemo(
    () =>
      new Map((layoutTemplatesQuery.data ?? []).map((template) => [template.templateId, template])),
    [layoutTemplatesQuery.data],
  );

  const invalidateFormats = async () => {
    await utils.admin.carouselFormats.list.invalidate({ activeOnly: false });
  };

  const createFormat = trpc.admin.carouselFormats.create.useMutation({
    onSuccess: async (format) => {
      setDraft(createCarouselFormatDraftFromOutput(format));
      await invalidateFormats();
    },
  });
  const updateFormat = trpc.admin.carouselFormats.update.useMutation({
    onSuccess: async (format) => {
      setDraft(createCarouselFormatDraftFromOutput(format));
      await invalidateFormats();
    },
  });
  const archiveFormat = trpc.admin.carouselFormats.archive.useMutation({
    onSuccess: async (format) => {
      setDraft(createCarouselFormatDraftFromOutput(format));
      await invalidateFormats();
    },
  });
  const activateFormat = trpc.admin.carouselFormats.activate.useMutation({
    onSuccess: async (format) => {
      setDraft(createCarouselFormatDraftFromOutput(format));
      await invalidateFormats();
    },
  });
  const reorderFormats = trpc.admin.carouselFormats.reorder.useMutation({
    onSuccess: invalidateFormats,
  });

  const mutationError =
    createFormat.error?.message ??
    updateFormat.error?.message ??
    archiveFormat.error?.message ??
    activateFormat.error?.message ??
    reorderFormats.error?.message ??
    null;
  const isMutating =
    createFormat.isPending ||
    updateFormat.isPending ||
    archiveFormat.isPending ||
    activateFormat.isPending ||
    reorderFormats.isPending;

  const updateDraft = <TKey extends keyof CarouselFormatDraft>(
    key: TKey,
    value: CarouselFormatDraft[TKey],
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const updatePatternRow = (
    index: number,
    patch: Partial<CarouselFormatDraft["pattern"][number]>,
  ) =>
    setDraft((current) => ({
      ...current,
      pattern: current.pattern.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    }));

  const selectFormat = (format: CarouselFormat) => {
    setDraft(createCarouselFormatDraftFromOutput(format));
    setActivePatternIndex(0);
  };

  const startNewFormat = () => {
    setDraft(createEmptyCarouselFormatDraft(getNextSortOrder(formats)));
    setActivePatternIndex(0);
  };

  const addPatternRow = () => {
    setDraft((current) => ({
      ...current,
      pattern: [...current.pattern, createCarouselPatternRowDraft()],
    }));
    setActivePatternIndex(draft.pattern.length);
  };

  const movePatternRow = (index: number, direction: -1 | 1) => {
    setDraft((current) => ({
      ...current,
      pattern: moveArrayItem(current.pattern, index, direction),
    }));
    setActivePatternIndex((current) => {
      if (current === index) {
        return current + direction;
      }
      if (current === index + direction) {
        return index;
      }
      return current;
    });
  };

  const removePatternRow = (index: number) => {
    setDraft((current) => ({
      ...current,
      pattern: current.pattern.filter((_, rowIndex) => rowIndex !== index),
    }));
    setActivePatternIndex((current) => {
      if (current > index) {
        return current - 1;
      }
      if (current === index) {
        return Math.max(0, index - 1);
      }
      return current;
    });
  };

  const toggleFormatTemplate = (template: LayoutTemplate, checked: boolean) =>
    setDraft((current) => ({
      ...current,
      templateOptions: checked
        ? [
            ...current.templateOptions.filter(
              (option) => option.templateId !== template.templateId,
            ),
            { templateId: template.templateId, templateName: template.name },
          ]
        : current.templateOptions.filter((option) => option.templateId !== template.templateId),
    }));

  const toggleSocialStyle = (style: BrandSocialContentStyle, checked: boolean) =>
    setDraft((current) => {
      const selected = new Set(current.socialAffinities.styles);
      if (checked) {
        selected.add(style);
      } else {
        selected.delete(style);
      }

      return {
        ...current,
        socialAffinities: {
          ...current.socialAffinities,
          styles: BRAND_SOCIAL_CONTENT_STYLES.filter((item) => selected.has(item)),
        },
      };
    });

  const toggleSocialMechanic = (mechanic: BrandSocialMechanic, checked: boolean) =>
    setDraft((current) => {
      const selected = new Set(current.socialAffinities.mechanics);
      if (checked) {
        selected.add(mechanic);
      } else {
        selected.delete(mechanic);
      }

      return {
        ...current,
        socialAffinities: {
          ...current.socialAffinities,
          mechanics: BRAND_SOCIAL_MECHANICS.filter((item) => selected.has(item)),
        },
      };
    });

  const saveDraft = () => {
    const payload = buildCarouselFormatWriteInput(draft);
    if (draft.id) {
      updateFormat.mutate({ id: draft.id, patch: payload });
      return;
    }

    createFormat.mutate(payload);
  };

  const archiveSelectedFormat = () => {
    if (draft.id) {
      archiveFormat.mutate({ id: draft.id });
    }
  };

  const activateSelectedFormat = () => {
    if (draft.id) {
      activateFormat.mutate({ id: draft.id });
    }
  };

  const reorder = (formatId: string, direction: -1 | 1) => {
    const orderedIds = moveCarouselFormatId(
      formats.map((format) => format.id),
      formatId,
      direction,
    );
    if (orderedIds.join("|") !== formats.map((format) => format.id).join("|")) {
      reorderFormats.mutate({ orderedIds });
    }
  };

  if (formatsQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (formatsQuery.error) {
    return (
      <QueryErrorState
        title="Carousel formats unavailable"
        message={formatsQuery.error.message}
        isForbidden={formatsQuery.error.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Carousel Formats</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Manage the narrative patterns used when carousel generation classifies, structures, and
            reviews multi-slide posts.
          </p>
        </div>
        <Button onClick={startNewFormat} variant="outline">
          <Plus className="size-4" />
          New format
        </Button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Format Library</CardTitle>
            <CardDescription>{formats.length} configured carousel patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {formats.length === 0 ? (
              <EmptyState title="No formats" description="Create the first carousel format." />
            ) : (
              formats.map((format, index) => (
                <div
                  key={format.id}
                  className={cn(
                    "rounded-md border p-3 transition-colors",
                    draft.id === format.id ? "border-primary bg-primary/5" : "bg-background",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => selectFormat(format)}
                    >
                      <span className="block truncate text-sm font-medium">{format.name}</span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">
                        {format.key}
                      </span>
                    </button>
                    <Badge variant={format.active ? "default" : "secondary"}>
                      {format.active ? "Active" : "Archived"}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => selectFormat(format)}>
                      <Pencil className="size-3.5" />
                      Edit
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Move ${format.name} up`}
                      disabled={index === 0 || isMutating}
                      onClick={() => reorder(format.id, -1)}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Move ${format.name} down`}
                      disabled={index === formats.length - 1 || isMutating}
                      onClick={() => reorder(format.id, 1)}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  {isEditing ? "Edit Carousel Format" : "Create Carousel Format"}
                </CardTitle>
                <CardDescription>
                  Format snapshots are copied into generation plans so future edits do not rewrite
                  existing plan history.
                </CardDescription>
              </div>
              {draft.active ? <Badge>Active</Badge> : <Badge variant="secondary">Archived</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 lg:grid-cols-2">
              <label className="space-y-1.5">
                <FieldLabel>Name</FieldLabel>
                <Input
                  value={draft.name}
                  onChange={(event) => updateDraft("name", event.target.value)}
                />
              </label>
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain format key"
                  tooltipContent="Stable machine-readable id for this format. Use lowercase words separated by underscores; generation snapshots keep this key in plan history."
                >
                  Key
                </FieldLabelWithHelp>
                <Input
                  aria-label="Key"
                  value={draft.key}
                  className="font-mono"
                  onChange={(event) => updateDraft("key", event.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain minimum slides"
                  tooltipContent="Lowest slide count this format may generate. The pattern row count must fit inside the minimum and maximum slide range."
                >
                  Min Slides
                </FieldLabelWithHelp>
                <Input
                  aria-label="Min Slides"
                  type="number"
                  min={SOCIAL_POST_CAROUSEL_MIN_SLIDES}
                  max={SOCIAL_POST_CAROUSEL_MAX_SLIDES}
                  value={draft.minSlides}
                  onChange={(event) => updateDraft("minSlides", Number(event.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain maximum slides"
                  tooltipContent="Highest slide count this format may generate. Keep this aligned with the number of pattern rows the planner can choose from."
                >
                  Max Slides
                </FieldLabelWithHelp>
                <Input
                  aria-label="Max Slides"
                  type="number"
                  min={SOCIAL_POST_CAROUSEL_MIN_SLIDES}
                  max={SOCIAL_POST_CAROUSEL_MAX_SLIDES}
                  value={draft.maxSlides}
                  onChange={(event) => updateDraft("maxSlides", Number(event.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain sort order"
                  tooltipContent="Manual priority for admin lists and generation selection. Lower numbers appear earlier."
                >
                  Sort Order
                </FieldLabelWithHelp>
                <Input
                  aria-label="Sort Order"
                  type="number"
                  min={0}
                  value={draft.sortOrder}
                  onChange={(event) => updateDraft("sortOrder", Number(event.target.value))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(event) => updateDraft("active", event.target.checked)}
                />
                Active for new carousel generation
              </label>
              <InfoTooltip
                label="Explain active format"
                content="When active, this format can be selected for future carousel generations. Archived formats remain available for historical plans."
                className="size-4"
              />
            </div>

            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <div>
                <FieldLabelWithHelp
                  tooltipLabel="Explain Content DNA affinities"
                  tooltipContent="Content DNA styles and platform-native mechanics this format naturally supports. Generation uses these affinities to match brand voice Content DNA and social creative briefs with compatible carousel structures."
                >
                  Content DNA Affinities
                </FieldLabelWithHelp>
                <p className="text-xs text-muted-foreground">
                  Pick the Content DNA style and platform-native mechanics this carousel format can
                  express well.
                </p>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="space-y-2">
                  <FieldLabel>Content DNA Styles</FieldLabel>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {BRAND_SOCIAL_CONTENT_STYLES.map((style) => {
                      const checked = draft.socialAffinities.styles.includes(style);
                      return (
                        <label
                          key={style}
                          className={cn(
                            "flex items-start gap-2 rounded-md border bg-background p-2 text-sm transition-colors hover:bg-muted/70",
                            checked ? "border-primary bg-primary/5" : null,
                          )}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 size-4 rounded border-input"
                            checked={checked}
                            onChange={(event) => toggleSocialStyle(style, event.target.checked)}
                          />
                          <span>{formatSnakeLabel(style)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <FieldLabel>Platform-native Mechanics</FieldLabel>
                  <div className="grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                    {BRAND_SOCIAL_MECHANICS.map((mechanic) => {
                      const checked = draft.socialAffinities.mechanics.includes(mechanic);
                      return (
                        <label
                          key={mechanic}
                          className={cn(
                            "flex items-start gap-2 rounded-md border bg-background p-2 text-sm transition-colors hover:bg-muted/70",
                            checked ? "border-primary bg-primary/5" : null,
                          )}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 size-4 rounded border-input"
                            checked={checked}
                            onChange={(event) =>
                              toggleSocialMechanic(mechanic, event.target.checked)
                            }
                          />
                          <span>{formatSnakeLabel(mechanic)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
                <div className="space-y-2">
                  <div>
                    <FieldLabelWithHelp
                      tooltipLabel="Explain layout templates"
                      tooltipContent="Published layouts eligible for this carousel format. Generation selects one layout for the complete carousel and reuses it across every slide."
                    >
                      Carousel Layout Templates
                    </FieldLabelWithHelp>
                    <p className="text-xs text-muted-foreground">
                      Select one or more eligible templates. The AI chooses one per carousel, and
                      every slide uses that same layout.
                    </p>
                  </div>

                  {layoutTemplatesQuery.isPending ? (
                    <p className="text-sm text-muted-foreground">
                      Loading published layout templates...
                    </p>
                  ) : layoutTemplatesQuery.error ? (
                    <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                      Failed to load published layout templates:{" "}
                      {layoutTemplatesQuery.error.message}
                    </p>
                  ) : publishedLayoutTemplates.length === 0 ? (
                    <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                      No published layout templates are available yet.
                    </p>
                  ) : (
                    <div className="max-h-80 space-y-1 overflow-y-auto rounded-md border bg-background p-2">
                      {publishedLayoutTemplates.map((template) => {
                        const checked = draft.templateOptions.some(
                          (option) => option.templateId === template.templateId,
                        );
                        return (
                          <label
                            key={template.templateId}
                            className={cn(
                              "grid cursor-pointer grid-cols-[auto_4rem_minmax(0,1fr)] items-start gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/70",
                              checked ? "bg-primary/5" : null,
                            )}
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              onChange={(event) =>
                                toggleFormatTemplate(template, event.target.checked)
                              }
                            />
                            <LayoutTemplateThumbnail
                              template={template}
                              label={`Template thumbnail for ${template.name}`}
                              fallbackTitle={template.name}
                              className="h-20 w-16"
                              maxWidth={64}
                            />
                            <span className="min-w-0">
                              <span className="block font-medium">{template.name}</span>
                              <span className="block truncate font-mono text-xs text-muted-foreground">
                                {template.templateId}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Shared Layout Preview</FieldLabel>
                  <AssignedTemplatePreviewPager
                    templateOptions={draft.templateOptions}
                    templatesById={publishedLayoutTemplatesById}
                    slideLabel="Carousel"
                  />
                  <p className="text-xs text-muted-foreground">
                    {draft.templateOptions.length > 0
                      ? `${draft.templateOptions.length} eligible template${draft.templateOptions.length === 1 ? "" : "s"}`
                      : "Any compatible published template is eligible."}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <FieldLabelWithHelp
                    tooltipLabel="Explain pattern rows"
                    tooltipContent="Ordered slide slots that tell the structure builder what each carousel slide should do, such as hook, proof, feature, or CTA."
                  >
                    Pattern Rows
                  </FieldLabelWithHelp>
                  <p className="text-xs text-muted-foreground">
                    Ordered slide roles used by the structure builder.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPatternRow}>
                  <Plus className="size-3.5" />
                  Add row
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex gap-2 overflow-x-auto rounded-lg border bg-muted/20 p-2">
                  {draft.pattern.map((row, index) => {
                    const isSelected = selectedPatternIndex === index;
                    const slideLabel = `Slide ${index + 1}`;
                    return (
                      <div
                        key={`${index}-${row.role}`}
                        className={cn(
                          "min-w-56 rounded-md border bg-background p-3 transition-colors",
                          isSelected
                            ? "border-primary shadow-sm ring-1 ring-primary/20"
                            : "hover:border-primary/40",
                        )}
                      >
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          className="w-full text-left outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => setActivePatternIndex(index)}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {slideLabel}
                            </span>
                            <Badge variant={row.required ? "default" : "secondary"}>
                              {row.role}
                            </Badge>
                          </span>
                          <span className="mt-2 block truncate text-sm font-medium">
                            {row.title || "Untitled slide"}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {selectedPatternRow ? (
                  <div className="rounded-md border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <FieldLabel>Selected Slide</FieldLabel>
                        <p className="text-sm font-medium">Slide {selectedPatternIndex + 1}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move pattern row ${selectedPatternIndex + 1} up`}
                          disabled={selectedPatternIndex === 0}
                          onClick={() => movePatternRow(selectedPatternIndex, -1)}
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move pattern row ${selectedPatternIndex + 1} down`}
                          disabled={selectedPatternIndex === draft.pattern.length - 1}
                          onClick={() => movePatternRow(selectedPatternIndex, 1)}
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Remove pattern row ${selectedPatternIndex + 1}`}
                          disabled={draft.pattern.length <= 2}
                          onClick={() => removePatternRow(selectedPatternIndex)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-3 lg:grid-cols-[150px_minmax(0,1fr)]">
                      <div className="space-y-1.5">
                        <FieldLabelWithHelp
                          tooltipLabel="Explain slide role"
                          tooltipContent="Semantic purpose for this slide. The planner uses the role to classify content and choose compatible copy/layout behavior."
                        >
                          Role
                        </FieldLabelWithHelp>
                        <Select
                          aria-label="Role"
                          value={selectedPatternRow.role}
                          onChange={(event) =>
                            updatePatternRow(selectedPatternIndex, {
                              role: event.target.value as CarouselFormatPatternRowDraft["role"],
                            })
                          }
                        >
                          {SOCIAL_POST_CAROUSEL_SLIDE_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <label className="space-y-1.5">
                        <FieldLabel>Title</FieldLabel>
                        <Input
                          value={selectedPatternRow.title}
                          onChange={(event) =>
                            updatePatternRow(selectedPatternIndex, { title: event.target.value })
                          }
                        />
                      </label>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <FieldLabelWithHelp
                        tooltipLabel="Explain slide guidance"
                        tooltipContent="Plain-language instruction for what the generator should include on this slide. Keep it specific to the slide role."
                      >
                        Guidance
                      </FieldLabelWithHelp>
                      <Textarea
                        aria-label="Guidance"
                        value={selectedPatternRow.guidance}
                        className="min-h-20"
                        onChange={(event) =>
                          updatePatternRow(selectedPatternIndex, { guidance: event.target.value })
                        }
                      />
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedPatternRow.required}
                          onChange={(event) =>
                            updatePatternRow(selectedPatternIndex, {
                              required: event.target.checked,
                            })
                          }
                        />
                        Required
                      </label>
                      <InfoTooltip
                        label="Explain required slide"
                        content="Required rows must appear in generated carousels. Optional rows can be skipped when the final slide count is lower."
                        className="size-4"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-3 xl:grid-cols-2">
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain classifier guidance"
                  tooltipContent="Instructions for deciding when this carousel format is the right narrative pattern for a generation request."
                >
                  Classifier Guidance
                </FieldLabelWithHelp>
                <Textarea
                  aria-label="Classifier Guidance"
                  value={draft.classifierGuidance}
                  onChange={(event) => updateDraft("classifierGuidance", event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabelWithHelp
                  tooltipLabel="Explain format example"
                  tooltipContent="Short example of the carousel story this format should produce. It gives reviewers and future edits a concrete reference."
                >
                  Example
                </FieldLabelWithHelp>
                <Textarea
                  aria-label="Example"
                  value={draft.example}
                  onChange={(event) => updateDraft("example", event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabelWithHelp
                tooltipLabel="Explain banned words"
                tooltipContent="Words or phrases the generator should avoid for this format. Enter one per line or separate them with commas."
              >
                Banned Words
              </FieldLabelWithHelp>
              <Textarea
                aria-label="Banned Words"
                value={draft.bannedWordsText}
                className="min-h-24 font-mono"
                placeholder="One word per line or comma-separated"
                onChange={(event) => updateDraft("bannedWordsText", event.target.value)}
              />
            </div>

            {validationErrors.length > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                <ul className="list-disc space-y-1 pl-5">
                  {validationErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {mutationError ? <p className="text-sm text-destructive">{mutationError}</p> : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={saveDraft} disabled={validationErrors.length > 0 || isMutating}>
                <Save className="size-4" />
                {isEditing ? "Save format" : "Create format"}
              </Button>
              {draft.id && draft.active ? (
                <Button variant="outline" disabled={isMutating} onClick={archiveSelectedFormat}>
                  <Archive className="size-4" />
                  Archive
                </Button>
              ) : draft.id ? (
                <Button variant="outline" disabled={isMutating} onClick={activateSelectedFormat}>
                  <RotateCcw className="size-4" />
                  Activate
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
